import { useState } from 'react';
import { Check, Copy, ShieldCheck, Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { useGround } from '@/hooks/useGround';
import { useToast } from '@/hooks/useToast';
import { PROTOCOLS, type Protocol } from '@/lib/ground/protocols';

/** A deterministic, demo-only LNURL-pay style invoice for a protocol. */
function demoInvoice(protocol: Protocol): string {
  let hash = 0;
  for (const ch of protocol.id) {
    hash = (hash * 31 + ch.charCodeAt(0)) % 0xffffffff;
  }
  const hex = hash.toString(16).padStart(8, '0').repeat(5).slice(0, 40);
  return `lnbc${protocol.priceSats * 10}n1p${hex}`;
}

interface PurchaseDialogProps {
  protocol: Protocol | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PurchaseDialog({ protocol, open, onOpenChange }: PurchaseDialogProps) {
  const { settings, updateSettings, purchase, purchases } = useGround();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  if (!protocol) return null;

  const invoice = demoInvoice(protocol);
  const included = protocol.kind === 'bundle'
    ? PROTOCOLS.filter((entry) => entry.kind !== 'bundle')
    : [];

  const confirm = () => {
    purchase(protocol.id);
    if (protocol.kind === 'bundle' && protocol.includes) {
      protocol.includes.forEach((id) => purchase(id));
    }
    toast({
      title: `${protocol.title} unlocked`,
      description: settings.demoMode
        ? 'Demo purchase — no real sats were spent.'
        : 'Payment received.',
    });
    onOpenChange(false);
  };

  const copyInvoice = async () => {
    try {
      await navigator.clipboard.writeText(invoice);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access is best-effort.
    }
  };

  const alreadyOwned = purchases.includes(protocol.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span aria-hidden>{protocol.emoji}</span>
            {protocol.title}
          </DialogTitle>
          <DialogDescription>{protocol.description}</DialogDescription>
        </DialogHeader>

        {included.length > 0 && (
          <ul className="space-y-1.5 rounded-lg border border-dashed p-4 text-sm">
            {included.map((entry) => (
              <li key={entry.id} className="flex items-center gap-2 text-muted-foreground">
                <Check className="size-3.5 text-primary" />
                <span className="text-foreground/90">{entry.title}</span>
                <span className="ml-auto tabular-nums">{entry.sessionCount} sessions</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="flex items-center gap-2">
            <Zap className="size-4 text-primary" />
            <span className="text-sm text-muted-foreground">Price</span>
          </div>
          <span className="font-display text-2xl font-semibold tabular-nums">
            {protocol.priceSats.toLocaleString()}
            <span className="ml-1 text-sm font-normal text-muted-foreground">sats</span>
          </span>
        </div>

        {settings.demoMode ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-4">
            <QRCodeCanvas value={invoice} size={168} className="rounded-md bg-white p-2" />
            <button
              type="button"
              onClick={copyInvoice}
              className="flex w-full items-center gap-2 rounded-md bg-secondary/60 px-3 py-2 text-left font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {copied ? <Check className="size-3.5 shrink-0 text-primary" /> : <Copy className="size-3.5 shrink-0" />}
              <span className="truncate">{invoice}</span>
            </button>
            <p className="text-center text-xs text-muted-foreground">
              Demo invoice — tap simulate to unlock without spending real sats.
            </p>
          </div>
        ) : (
          <div className="space-y-3 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            <p className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 size-4 shrink-0 text-primary" />
              Real payments are sent over Lightning using your NWC wallet. Add an NWC connection
              string in the Wallet tab to pay for real.
            </p>
            <Button variant="outline" size="sm" onClick={() => updateSettings({ demoMode: true })}>
              Enable demo mode instead
            </Button>
          </div>
        )}

        <DialogFooter>
          {settings.demoMode && (
            <Button onClick={confirm} className="gap-2" disabled={alreadyOwned}>
              <Zap className="size-4" />
              {alreadyOwned ? 'Already unlocked' : 'Simulate payment'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
